import { run1 }  from '../runs/run1-font';
import { run2 }  from '../runs/run2-margins';
import { run3 }  from '../runs/run3-tableCaption';
import { run4 }  from '../runs/run4-hyphens';
import { run5 }  from '../runs/run5-spaces';
import { run6 }  from '../runs/run6-bodyText';
import { run7 }  from '../runs/run7-bullets';
import { run8 }  from '../runs/run8-figures';
import { run9 }  from '../runs/run9-figureFootnotes';
import { run10 } from '../runs/run10-headings';
import { run11 } from '../runs/run11-tableText';
import { run12 } from '../runs/run12-tableFootnotes';
import { run13 } from '../runs/run13-LOT';
import { run14 } from '../runs/run14-LOF';
import { run15 } from '../runs/run15-LOA';
import { run16 } from '../runs/run16-appendix';
import { run17 } from '../runs/run17_paraformat';
import { run18 } from '../runs/run18-headerformat-qc-16';
import { run19 } from '../runs/run19-footerformat-qc-17';
import { run20 } from '../runs/run20-consistent_header-qc-20.01';
import { run21 } from '../runs/run21-remove_footnote';
import { run22 } from '../runs/run22-add_confidential';
import { run23 } from '../runs/run23-sectionspacing';
import { run24 } from '../runs/run24-crossReferences';
import { run25 } from '../runs/run25-tableListBullets';
import { run26 } from '../runs/run26-tableDefaults';
import { run27 } from '../runs/run27-nonbreakingSpaces';

import { initDocPilot } from './docpilotUI';
import { setSafeAlertCallback, safeAlert, RunDetail } from '../runs/common';
import { TEMPLATE_BASE64, TEMPLATE_FILE_NAME } from "./templateData";
import helpConfig from "./helpConfig.json";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface RunConfig {
  id: string;
  label: string;
}

interface CheckResult {
  name: string;
  status: "pending" | "running" | "done" | "error";
  message: string;
  details: RunDetail[];
  timestamp: Date | null;
  durationMs?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// RUN CONFIG — loaded from runsConfig.json, order preserved from JSON array
// ═══════════════════════════════════════════════════════════════════════════

let RUNS_CONFIG: RunConfig[] = [];

// ═══════════════════════════════════════════════════════════════════════════
// CHECK RESULT TRACKING
// ═══════════════════════════════════════════════════════════════════════════

let TOTAL_CHECKS = 0;
const qcResults: Map<string, CheckResult> = new Map();
let currentRunId: string | null = null;
let lastCompletedRunId: string | null = null;

const RUN_FUNCTIONS: Map<string, () => Promise<any>> = new Map<string, () => Promise<any>>();
RUN_FUNCTIONS.set("run1",  run1);  RUN_FUNCTIONS.set("run2",  run2);
RUN_FUNCTIONS.set("run3",  run3);  RUN_FUNCTIONS.set("run4",  run4);
RUN_FUNCTIONS.set("run5",  run5);  RUN_FUNCTIONS.set("run6",  run6);
RUN_FUNCTIONS.set("run7",  run7);  RUN_FUNCTIONS.set("run8",  run8);
RUN_FUNCTIONS.set("run9",  run9);  RUN_FUNCTIONS.set("run10", run10);
RUN_FUNCTIONS.set("run11", run11); RUN_FUNCTIONS.set("run12", run12);
RUN_FUNCTIONS.set("run13", run13); RUN_FUNCTIONS.set("run14", run14);
RUN_FUNCTIONS.set("run15", run15); RUN_FUNCTIONS.set("run16", run16);
RUN_FUNCTIONS.set("run17", run17); RUN_FUNCTIONS.set("run18", run18);
RUN_FUNCTIONS.set("run19", run19); RUN_FUNCTIONS.set("run20", run20);
RUN_FUNCTIONS.set("run21", run21); RUN_FUNCTIONS.set("run22", run22);
RUN_FUNCTIONS.set("run23", run23); RUN_FUNCTIONS.set("run24", run24);
RUN_FUNCTIONS.set("run25", run25); RUN_FUNCTIONS.set("run26", run26);
RUN_FUNCTIONS.set("run27", run27);

// ═══════════════════════════════════════════════════════════════════════════
// SAFE ALERT CALLBACK
// ═══════════════════════════════════════════════════════════════════════════

// S7735: positive condition instead of negated early-return
function onSafeAlert(msg: string, details: RunDetail[] = []): void {
  if (currentRunId && qcResults.has(currentRunId)) {
    const result = qcResults.get(currentRunId);
    result.status = "done";
    result.message = msg;
    result.details = details || [];
    result.timestamp = new Date();
    lastCompletedRunId = currentRunId;
    updateStatusBadge(currentRunId);
    completeProgressBar(currentRunId, true);
    currentRunId = null;
  }
}

setSafeAlertCallback(onSafeAlert);

function updateStatusBadge(runId: string): void {
  const badge = document.getElementById("status-" + runId);
  const result = qcResults.get(runId);
  if (badge && result) {
    badge.className = "check-status";
    if (result.status === "running") {
      badge.textContent = "\u23F3";
      badge.classList.add("running");
      const est = progressEstimates.get(runId);
      badge.title = est === undefined
        ? "Running\u2026"
        : "Running\u2026 ~" + formatDuration(est) + " estimated";
    } else if (result.status === "done") {
      badge.textContent = "\u2705";
      badge.classList.add("done");
      badge.title = result.message.split("\n")[0];
    } else if (result.status === "error") {
      badge.textContent = "\u26A0\uFE0F";
      badge.classList.add("error");
      badge.title = "Error occurred";
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// PER-CHECK PROGRESS BAR — adaptive, document-aware
// ═══════════════════════════════════════════════════════════════════════════

interface CheckProfile { baseMs: number; sizeSensitivity: number; }
interface RunTiming   { durationMs: number; complexityFactor: number; timestamp: number; }

const DEFAULT_PROFILE: CheckProfile = { baseMs: 5000, sizeSensitivity: 0.8 };
const TIMING_FLOOR_MS  = 800;
const TIMING_CEILING_MS = 240000;
const TIMINGS_PER_CHECK = 10;
const TIMINGS_STORAGE_KEY = "qcProgressTimings_v1";

// S7748: no trailing zero fractions — e.g. 0.10 → 0.1, 1.00 → 1
const CHECK_PROFILE: Record<string, CheckProfile> = {
  run2:  { baseMs:  800, sizeSensitivity: 0.05 },
  run22: { baseMs: 1200, sizeSensitivity: 0.1  },
  run4:  { baseMs: 1800, sizeSensitivity: 0.6  },
  run5:  { baseMs: 2200, sizeSensitivity: 0.7  },
  run13: { baseMs: 1800, sizeSensitivity: 0.3  },
  run14: { baseMs: 1800, sizeSensitivity: 0.3  },
  run15: { baseMs: 1800, sizeSensitivity: 0.3  },
  run18: { baseMs: 2500, sizeSensitivity: 0.3  },
  run19: { baseMs: 2500, sizeSensitivity: 0.3  },
  run20: { baseMs: 2500, sizeSensitivity: 0.3  },
  run21: { baseMs: 1800, sizeSensitivity: 0.2  },
  run23: { baseMs: 3000, sizeSensitivity: 0.5  },
  run27: { baseMs: 2000, sizeSensitivity: 0.7  },
  run1:  { baseMs: 3500, sizeSensitivity: 0.9  },
  run6:  { baseMs: 3500, sizeSensitivity: 0.9  },
  run8:  { baseMs: 3500, sizeSensitivity: 0.8  },
  run9:  { baseMs: 4500, sizeSensitivity: 0.8  },
  run10: { baseMs: 6000, sizeSensitivity: 0.85 },
  run16: { baseMs: 4000, sizeSensitivity: 0.7  },
  run3:  { baseMs: 5000, sizeSensitivity: 0.9  },
  run7:  { baseMs: 5500, sizeSensitivity: 1    },
  run11: { baseMs: 5500, sizeSensitivity: 1    },
  run12: { baseMs: 8000, sizeSensitivity: 1    },
  run17: { baseMs: 7000, sizeSensitivity: 1    },
  run24: { baseMs: 7000, sizeSensitivity: 0.95 },
  run25: { baseMs: 7000, sizeSensitivity: 1    },
  run26: { baseMs: 5000, sizeSensitivity: 0.9  },
};

// ── Document complexity ────────────────────────────────────────────────
let _docComplexityFactor   = 1;
let _docComplexityMeasured = false;
let _measurementInflight   = false;

async function measureDocumentComplexity(force: boolean = false): Promise<void> {
  if (_measurementInflight) return;
  if (_docComplexityMeasured && !force) return;
  _measurementInflight = true;

  try {
    await Word.run(async (context) => {
      const paras  = context.document.body.paragraphs;
      const tables = context.document.body.tables;
      paras.load("items");
      tables.load("items");
      await context.sync();

      const paraCount  = paras.items.length;
      const tableCount = tables.items.length;

      const paraFactor = Math.max(0.25, paraCount / 300);
      const tableBonus = 1 + tableCount * 0.04;
      _docComplexityFactor   = paraFactor * tableBonus;
      _docComplexityMeasured = true;

      console.log(
        `[Progress] Document complexity measured: ${paraCount} paragraphs, ${tableCount} tables → factor ${_docComplexityFactor.toFixed(2)}x`
      );
    });
  } catch (e) {
    console.warn("[Progress] Could not measure document complexity, falling back to default factor:", e);
  } finally {
    _measurementInflight = false;
  }
}

// ── Adaptive timing history (localStorage) ─────────────────────────────

function loadTimingHistory(): Record<string, RunTiming[]> {
  // S2486: localStorage may be unavailable (private mode, quota) — log and degrade.
  try {
    const raw = window.localStorage.getItem(TIMINGS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.warn("[Progress] Could not load timing history:", e);
    return {};
  }
}

function saveRunTiming(checkId: string, durationMs: number, complexityFactor: number): void {
  if (durationMs < 200 || durationMs > TIMING_CEILING_MS) return;

  // S2486: localStorage write failure — log and degrade gracefully.
  try {
    const all = loadTimingHistory();
    const list = all[checkId] ?? [];
    list.push({ durationMs, complexityFactor, timestamp: Date.now() });
    while (list.length > TIMINGS_PER_CHECK) list.shift();
    all[checkId] = list;
    window.localStorage.setItem(TIMINGS_STORAGE_KEY, JSON.stringify(all));
  } catch (e) {
    console.warn("[Progress] Could not save run timing:", e);
  }
}

function getEstimatedDuration(checkId: string, complexityFactor: number): number {
  const profile = CHECK_PROFILE[checkId] ?? DEFAULT_PROFILE;
  const sens    = profile.sizeSensitivity;

  const scaleFor = (factor: number) => 1 + sens * (factor - 1);
  const staticEstimate = profile.baseMs * scaleFor(complexityFactor);

  const history = loadTimingHistory()[checkId] ?? [];
  if (history.length === 0) {
    return clamp(staticEstimate, TIMING_FLOOR_MS, TIMING_CEILING_MS);
  }

  const normalised = history
    .map(t => t.durationMs / Math.max(0.01, scaleFor(t.complexityFactor)))
    .filter(v => v > 0 && Number.isFinite(v));
  const avgNormalised = normalised.reduce((a, b) => a + b, 0) / normalised.length;
  const historicalEstimate = avgNormalised * scaleFor(complexityFactor);

  const historyWeight = Math.min(history.length / 6, 0.75);
  const blended = historicalEstimate * historyWeight + staticEstimate * (1 - historyWeight);

  return clamp(blended, TIMING_FLOOR_MS, TIMING_CEILING_MS);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function formatDuration(ms: number): string {
  if (ms < 1000) return Math.round(ms) + "ms";
  const s = ms / 1000;
  if (s < 60) return s < 10 ? s.toFixed(1) + "s" : Math.round(s) + "s";
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return m + "m " + r + "s";
}

// ── Progress bar state & visuals ───────────────────────────────────────

const progressIntervals: Map<string, number> = new Map();
const progressStartTimes: Map<string, number> = new Map();
const progressEstimates:  Map<string, number> = new Map();

function progressColor(pct: number): string {
  const t   = Math.max(0, Math.min(1, pct / 100));
  const hue = 45 + (120 - 45) * t;
  return `hsl(${hue.toFixed(1)}, 80%, ${(55 - 5 * t).toFixed(1)}%)`;
}

function progressGlow(pct: number): string {
  const t   = Math.max(0, Math.min(1, pct / 100));
  const hue = 45 + (120 - 45) * t;
  return `0 0 6px hsla(${hue.toFixed(1)}, 85%, 55%, 0.65)`;
}

// S7748: integer literal `100` — no trailing ".0"
function startProgressBar(id: string): void {
  const fill = document.getElementById("progress-" + id);
  if (!fill) return;

  const existing = progressIntervals.get(id);
  if (existing !== undefined) { window.clearInterval(existing); progressIntervals.delete(id); }

  fill.style.transition       = "none";
  fill.style.width            = "0%";
  fill.style.backgroundColor  = progressColor(0);
  fill.style.boxShadow        = progressGlow(0);
  fill.style.opacity          = "1";
  const _reflow1 = fill.offsetWidth; // intentional reflow to flush transition reset
  fill.style.transition       = "";

  const expectedMs = getEstimatedDuration(id, _docComplexityFactor);
  progressEstimates.set(id, expectedMs);

  const ratePerMs = 2.575 / expectedMs;
  const asymptote = 92;
  const startMs   = Date.now();
  progressStartTimes.set(id, startMs);

  const tick = () => {
    const elapsed = Date.now() - startMs;
    const pct = asymptote * (1 - Math.exp(-ratePerMs * elapsed));
    fill.style.width           = pct.toFixed(1) + "%";
    fill.style.backgroundColor = progressColor(pct);
    fill.style.boxShadow       = progressGlow(pct);
  };
  tick();
  const intervalId = window.setInterval(tick, 120);
  progressIntervals.set(id, intervalId);
}

function completeProgressBar(id: string, ok: boolean = true): void {
  const intervalId = progressIntervals.get(id);
  if (intervalId !== undefined) { window.clearInterval(intervalId); progressIntervals.delete(id); }

  const startTime = progressStartTimes.get(id);
  if (startTime !== undefined) {
    progressStartTimes.delete(id);
    const actualMs = Date.now() - startTime;
    // Record how long the check took so the QC Summary can show it.
    const res = qcResults.get(id);
    if (res) res.durationMs = actualMs;
    if (ok) {
      saveRunTiming(id, actualMs, _docComplexityFactor);
      const predicted = progressEstimates.get(id);
      if (predicted !== undefined) {
        console.log(
          `[Progress] ${id} actual=${formatDuration(actualMs)} predicted=${formatDuration(predicted)} factor=${_docComplexityFactor.toFixed(2)}x`
        );
      }
    }
  }
  progressEstimates.delete(id);

  const fill = document.getElementById("progress-" + id);
  if (!fill) return;

  if (ok) {
    fill.style.width           = "100%";
    fill.style.backgroundColor = progressColor(100);
    fill.style.boxShadow       = progressGlow(100);
  } else {
    fill.style.backgroundColor = "#d13438";
    fill.style.boxShadow       = "0 0 6px rgba(209, 52, 56, 0.6)";
  }

  window.setTimeout(() => {
    fill.style.transition = "opacity 0.35s ease";
    fill.style.opacity    = "0";
    window.setTimeout(() => {
      fill.style.transition      = "none";
      fill.style.width           = "0%";
      fill.style.opacity         = "1";
      fill.style.backgroundColor = progressColor(0);
      fill.style.boxShadow       = progressGlow(0);
      const _reflow2 = fill.offsetWidth; // intentional reflow to flush transition reset
      fill.style.transition      = "";
    }, 380);
  }, 380);
}

// ═══════════════════════════════════════════════════════════════════════════
// RUN QUEUE — prevents concurrent Word.run calls that crash Win32 WebView
// ═══════════════════════════════════════════════════════════════════════════

interface QueuedRun {
  id: string;
  fn: () => Promise<any>;
  resolve: () => void;
}

let isAnyCheckRunning = false;
let pendingQueue: QueuedRun[] = [];

async function executeNow(id: string, fn: () => Promise<any>): Promise<void> {
  const el = document.getElementById(id);
  const result = qcResults.get(id);

  currentRunId = id;
  if (result) { result.status = "running"; result.message = ""; result.details = []; result.durationMs = undefined; }
  updateStatusBadge(id);
  if (el) { el.classList.add("check-running"); el.classList.remove("queued"); }
  startProgressBar(id);

  try {
    await fn();
  } catch (e) {
    console.error(`Error in ${id}:`, e);
    if (currentRunId === id) {
      const r = qcResults.get(id);
      if (r) { r.status = "error"; r.message = (e as Error).message || "Unknown error"; r.timestamp = new Date(); lastCompletedRunId = id; }
      updateStatusBadge(id);
      completeProgressBar(id, false);
      currentRunId = null;
    }
  } finally {
    if (el) el.classList.remove("check-running");
  }
}

function safeExecuteRun(id: string, fn: () => Promise<any>): Promise<void> {
  return new Promise<void>((resolve) => {
    if (isAnyCheckRunning) {
      const alreadyQueued = pendingQueue.some(q => q.id === id);
      if (alreadyQueued) {
        console.log(`[Queue] ${id} already queued, ignoring duplicate`);
        resolve();
        return;
      }
      console.log(`[Queue] ${id} \u2192 queued (${pendingQueue.length + 1} in queue)`);
      const el = document.getElementById(id);
      const result = qcResults.get(id);
      if (el) el.classList.add("queued");
      if (result) { result.message = "Queued..."; }
      const badge = document.getElementById("status-" + id);
      if (badge) { badge.textContent = "\u23F3"; badge.className = "check-status queued"; badge.title = "Queued..."; }
      pendingQueue.push({ id, fn, resolve });
      return;
    }

    isAnyCheckRunning = true;
    (async () => {
      try {
        await executeNow(id, fn);
      } finally {
        isAnyCheckRunning = false;
        resolve();
        if (pendingQueue.length > 0) {
          const next = pendingQueue.shift();
          if (next) {
            console.log(`[Queue] Auto-starting queued check: ${next.id}`);
            safeExecuteRun(next.id, next.fn).then(next.resolve);
          }
        }
      }
    })();
  });
}

function wrapRunButton(id: string, fn: () => Promise<any>): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.onclick = () => { safeExecuteRun(id, fn); };
}

// ═══════════════════════════════════════════════════════════════════════════
// RENDER CHECK CARDS — fetches runsConfig.json, preserves JSON array order
// ═══════════════════════════════════════════════════════════════════════════

async function renderCheckCards(): Promise<void> {
  const list = document.getElementById("checks-list");
  if (!list) return;

  // S2486: failure to load config is fatal for rendering — log the error.
  try {
    RUNS_CONFIG = await fetch("runsConfig.json").then(r => r.json());
  } catch (e) {
    console.error("[renderCheckCards] Failed to load runsConfig.json:", e);
    return;
  }

  TOTAL_CHECKS = RUNS_CONFIG.length;
  for (const run of RUNS_CONFIG) {
    qcResults.set(run.id, { name: run.label, status: "pending", message: "", details: [], timestamp: null });
  }

  for (let i = 0; i < RUNS_CONFIG.length; i++) {
    const run = RUNS_CONFIG[i];
    const displayNum = i + 1;
    const card = document.createElement("div");
    card.className = "ck-card";
    card.id = run.id;
    card.setAttribute("role", "button");
    const searchIndex = (run.label + " " + displayNum + " " + run.id).toLowerCase();
    // S7761: use .dataset instead of setAttribute("data-search", ...)
    card.dataset.search = searchIndex;
    card.innerHTML =
      `<input type="checkbox" class="check-cb run-cb" data-run="${run.id}" />` +
      `<div class="ck-num">${displayNum}</div>` +
      `<div class="ck-label">${run.label}</div>` +
      `<span class="check-status" id="status-${run.id}"></span>` +
      `<div class="ck-progress"><div class="ck-progress-fill" id="progress-${run.id}"></div></div>`;
    list.appendChild(card);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// RENDER HELP PANEL FROM JSON CONFIG
// ═══════════════════════════════════════════════════════════════════════════

// S3776: extracted from renderHelpPanel to reduce its cognitive complexity
function buildHelpCardHtml(card: any, index: number): string {
  const isTemplate = card.type === "template";
  const isTip      = card.type === "tip";
  // S3358: no nested ternary — use if/else
  let typeClass: string;
  if (isTemplate)   { typeClass = " help-card-template"; }
  else if (isTip)   { typeClass = " help-card-tip"; }
  else              { typeClass = ""; }

  const iconHtml = (isTemplate || isTip)
    ? '<div class="help-card-icon">' + (card.icon || "") + '</div>'
    : '<div class="help-card-num">' + card.num + '</div>';

  let tagsHtml = "";
  if (card.tags && card.tags.length > 0) {
    tagsHtml = '<div class="help-tag-row">' + card.tags.map((t: string) => '<span class="help-tag">' + t + '</span>').join("") + '</div>';
  }
  let baHtml = "";
  if (card.before && card.after) {
    baHtml = '<div class="help-ba"><div class="ba-before"><strong>Before:</strong> ' + card.before + '</div><div class="ba-arrow">&#8594;</div><div class="ba-after"><strong>After:</strong> ' + card.after + '</div></div>';
  }
  let tipsHtml = "";
  if (card.tips && card.tips.length > 0) {
    tipsHtml = '<ul class="help-tips-list">' + card.tips.map((t: string) => '<li>' + t + '</li>').join("") + '</ul>';
  }
  const descHtml = card.description ? '<p>' + card.description + '</p>' : '';
  let html = '<div class="help-card' + typeClass + '" data-keywords="' + (card.keywords || "") + '">';
  html += '<div class="help-card-header">' + iconHtml + '<span class="help-card-title">' + card.title + '</span><span class="help-card-arrow">&#9660;</span></div>';
  html += '<div class="help-card-detail">' + descHtml + tagsHtml + baHtml + tipsHtml + '</div></div>';
  return html;
}

function renderHelpPanel(): void {
  const container = document.getElementById("helpContent");
  if (!container) return;
  let html = "";
  for (let i = 0; i < (helpConfig as any).cards.length; i++) {
    html += buildHelpCardHtml((helpConfig as any).cards[i], i);
  }
  container.innerHTML = html;
}

// ═══════════════════════════════════════════════════════════════════════════
// OFFICE.ONREADY
// ═══════════════════════════════════════════════════════════════════════════

Office.onReady(async (info) => {
  if (info.host === Office.HostType.Word) {
    document.getElementById("sideload-msg").style.display = "none";
    document.getElementById("app-body").style.display = "flex";

    await renderCheckCards();

    for (const run of RUNS_CONFIG) {
      const fn = RUN_FUNCTIONS.get(run.id);
      if (fn) wrapRunButton(run.id, fn);
    }

    document.getElementById("loadTemplateBtn").onclick = loadTemplate;

    document.getElementById("helpToggleBtn").onclick = () => {
      document.getElementById("help-panel").style.display = "block";
    };
    document.getElementById("helpCloseBtn").onclick = () => {
      document.getElementById("help-panel").style.display = "none";
    };

    renderHelpPanel();

    document.querySelectorAll(".help-card-header").forEach((header) => {
      header.addEventListener("click", () => {
        const detail = header.nextElementSibling as HTMLElement;
        const arrow  = header.querySelector(".help-card-arrow");
        if (!detail) return;
        const isOpen = detail.classList.contains("open");
        document.querySelectorAll(".help-card-detail.open").forEach((d) => d.classList.remove("open"));
        document.querySelectorAll(".help-card-arrow.open").forEach((a) => a.classList.remove("open"));
        if (!isOpen) { detail.classList.add("open"); if (arrow) arrow.classList.add("open"); }
      });
    });

    const searchInput = document.getElementById("helpSearchInput") as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener("input", () => {
        const query = searchInput.value.toLowerCase().trim();
        document.querySelectorAll("#helpContent .help-card").forEach((card: HTMLElement) => {
          if (!query) { card.classList.remove("hidden"); return; }
          const keywords = (card.dataset.keywords || "").toLowerCase();
          // S6582: optional chaining instead of manual && guard
          const title = (card.querySelector(".help-card-title")?.textContent || "").toLowerCase();
          card.classList.toggle("hidden", !keywords.includes(query) && !title.includes(query));
        });
      });
    }

    document.getElementById("summaryBtn").onclick = openSummary;
    document.getElementById("summaryCloseBtn").onclick = () => {
      document.getElementById("summary-panel").style.display = "none";
    };

    document.getElementById("runSelectedBtn").onclick = runSelectedChecks;

    const selectAllCb = document.getElementById("selectAllCb") as HTMLInputElement;
    if (selectAllCb) {
      selectAllCb.onchange = () => {
        document.querySelectorAll(".run-cb").forEach((cb: HTMLInputElement) => {
          const card = cb.closest(".ck-card") as HTMLElement | null;
          if (card?.classList.contains("search-hidden")) return;
          cb.checked = selectAllCb.checked;
        });
        updateRunSelectedBtn();
      };
    }

    document.querySelectorAll(".run-cb").forEach((cb: HTMLInputElement) => {
      cb.addEventListener("click", (e) => { e.stopPropagation(); });
      cb.onchange = () => {
        const visibleCbs = Array.from(
          document.querySelectorAll(".run-cb")
        ).filter((c) => {
          const card = (c as HTMLElement).closest(".ck-card") as HTMLElement | null;
          return card && !card.classList.contains("search-hidden");
        }) as HTMLInputElement[];

        const allChecked  = visibleCbs.length > 0 && visibleCbs.every((c) => c.checked);
        const noneChecked = visibleCbs.every((c) => !c.checked);
        if (selectAllCb) { selectAllCb.checked = allChecked; selectAllCb.indeterminate = !allChecked && !noneChecked; }
        updateRunSelectedBtn();
      };
    });

    updateRunSelectedBtn();
    setupChecksSearch();

    measureDocumentComplexity().catch(() => { /* already logged in measureDocumentComplexity */ });
  }
});

// Initialize DocPilot AI (optional feature)
initDocPilot(RUN_FUNCTIONS);

// ═══════════════════════════════════════════════════════════════════════════
// TEMPLATE LOADER
// ═══════════════════════════════════════════════════════════════════════════

function setTemplateStatus(msg: string, isError = false): void {
  const el = document.getElementById("template-status");
  if (el) { el.textContent = msg; el.style.color = isError ? "#d13438" : "#107c10"; }
}

function showConfirmDialog(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.4);z-index:9999;display:flex;align-items:center;justify-content:center;";
    const box = document.createElement("div");
    box.style.cssText = "background:#fff;border-radius:8px;padding:24px;max-width:300px;width:90%;box-shadow:0 4px 20px rgba(0,0,0,0.3);font-family:Segoe UI,sans-serif;";
    const msgEl = document.createElement("p");
    msgEl.style.cssText = "margin:0 0 20px 0;font-size:14px;line-height:1.5;color:#333;white-space:pre-line;";
    msgEl.textContent = message;
    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:10px;justify-content:flex-end;";
    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.cssText = "padding:8px 20px;border:1px solid #ccc;background:#fff;border-radius:4px;cursor:pointer;font-size:13px;";
    cancelBtn.onclick = () => { overlay.remove(); resolve(false); };
    const okBtn = document.createElement("button");
    okBtn.textContent = "Confirm";
    okBtn.style.cssText = "padding:8px 20px;border:none;background:#0078d4;color:#fff;border-radius:4px;cursor:pointer;font-size:13px;";
    okBtn.onclick = () => { overlay.remove(); resolve(true); };
    btnRow.appendChild(cancelBtn); btnRow.appendChild(okBtn);
    box.appendChild(msgEl); box.appendChild(btnRow);
    overlay.appendChild(box); document.body.appendChild(overlay);
  });
}

// S4325: removed unnecessary `as HTMLElement` assertions on getElementById calls.
// S3735: removed `void` operator before promise calls.
export async function loadTemplate(): Promise<void> {
  if (!TEMPLATE_BASE64 || TEMPLATE_FILE_NAME === "PLACEHOLDER") {
    safeAlert("No template bundled!\n\nRun this command in the project root:\n  node convert-template-to-base64.js path/to/your-template.dotx\n\nThen rebuild with: npm run build:dev");
    return;
  }
  const confirmed = await showConfirmDialog("Load SRS Template?\n\nTemplate: " + TEMPLATE_FILE_NAME + "\n\nThis will open a NEW document with the full template (styles, headers, footers, margins).");
  if (!confirmed) return;
  try {
    setTemplateStatus("Loading template...");
    await Word.run(async (context) => {
      const newDoc = context.application.createDocument(TEMPLATE_BASE64); await context.sync();
      newDoc.open(); await context.sync();
      setTemplateStatus("\u2713 Template loaded: " + TEMPLATE_FILE_NAME);
    });
    _docComplexityMeasured = false;
    measureDocumentComplexity().catch(() => { /* already logged */ });
  } catch (error) {
    console.error("[Template] Load error:", error);
    setTemplateStatus("Error loading template.", true);
    safeAlert("Error loading template:\n" + (error as Error).message);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// QC SUMMARY REPORT
// ═══════════════════════════════════════════════════════════════════════════

// ── helpers extracted from openSummary to reduce cognitive complexity ──

function buildSummaryStatsHtml(totalRun: number, totalDone: number, pending: number): string {
  let html = '<div class="summary-stats">';
  html += '<div class="summary-stat"><span class="summary-stat-num stat-run">' + totalRun + '</span><span class="summary-stat-label">Run</span></div>';
  html += '<div class="summary-stat"><span class="summary-stat-num stat-pass">' + totalDone + '</span><span class="summary-stat-label">Passed</span></div>';
  html += '<div class="summary-stat"><span class="summary-stat-num stat-pending">' + pending + '</span><span class="summary-stat-label">Pending</span></div>';
  html += '</div>';
  return html;
}

function buildDetailRowHtml(d: RunDetail): string {
  if (d.bookmarkId) {
    const esc = d.label.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    let row = '<div class="sr-body-row sr-nav-row" data-bookmark-id="' + d.bookmarkId + '" title="Click to go to exact position">';
    row += '<span class="sr-nav-arrow">&#8594;</span><span class="sr-nav-text">' + esc + '</span>';
    if (d.note) row += '<span class="sr-nav-note">' + d.note + '</span>';
    row += '<span class="sr-nav-btn">Go &#8599;</span></div>';
    return row;
  }
  if (d.paraId) {
    const esc = d.label.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    let row = '<div class="sr-body-row sr-nav-row" data-para-id="' + d.paraId + '" title="Click to go to this paragraph">';
    row += '<span class="sr-nav-arrow">&#8594;</span><span class="sr-nav-text">' + esc + '</span>';
    if (d.note) row += '<span class="sr-nav-note">' + d.note + '</span>';
    row += '<span class="sr-nav-btn">Go &#8599;</span></div>';
    return row;
  }
  let row = '<div class="sr-body-row"><span class="sr-body-label">' + d.label + '</span><span class="sr-body-vals">';
  if (d.count !== undefined) row += '<span class="sr-body-count">' + d.count + '</span>';
  if (d.note) row += '<span class="sr-body-note">' + d.note + '</span>';
  row += '</span></div>';
  return row;
}

function buildSummaryCardHtml(run: RunConfig, index: number): string {
  const id = run.id;
  const r = qcResults.get(id);
  if (!r) return "";

  const isDone  = r.status === "done";
  const isError = r.status === "error";
  // S3358: no nested ternaries — use if/else
  let cardCls: string;
  if (isDone)       { cardCls = "done";    }
  else if (isError) { cardCls = "error";   }
  else              { cardCls = "pending"; }

  let icon: string;
  if (isDone)       { icon = "\u2705";        }
  else if (isError) { icon = "\u26A0\uFE0F";  }
  else              { icon = "\u23F3";         }

  let timeStr = "";
  if (typeof r.durationMs === "number") {
    timeStr = formatDuration(r.durationMs);
  }
  let finishedAt = "";
  if (r.timestamp) {
    finishedAt = "Finished at " +
      r.timestamp.getHours().toString().padStart(2, "0") + ":" +
      r.timestamp.getMinutes().toString().padStart(2, "0") + ":" +
      r.timestamp.getSeconds().toString().padStart(2, "0");
  }

  const hasDetails = isDone && Array.isArray(r.details) && r.details.length > 0;

  let html = '<div class="sr-card ' + cardCls + '">';
  html += hasDetails
    ? '<div class="sr-header sr-clickable" data-id="' + id + '">'
    : '<div class="sr-header">';
  html += '<span class="sr-num">' + (index + 1) + '</span>';
  html += '<span class="sr-name">' + r.name + '</span>';
  html += '<span class="sr-right">';
  html += '<span class="sr-icon">' + icon + '</span>';
  if (timeStr) {
    const titleAttr = finishedAt ? ' title="' + finishedAt + '"' : '';
    html += '<span class="sr-time"' + titleAttr + '>\u23F1 ' + timeStr + '</span>';
  }
  if (hasDetails) html += '<span class="sr-chev" data-id="' + id + '">\u25BC</span>';
  html += '</span></div>';

  if (hasDetails) {
    html += '<div class="sr-body" id="srbody-' + id + '">';
    html += '<div class="sr-body-title">' + r.message.split("\n")[0] + '</div>';
    for (const d of r.details) {
      html += buildDetailRowHtml(d);
    }
    html += '</div>';
  }
  html += '</div>';
  return html;
}

// navigation helper extracted from the nav-row click handler
async function findAndSelectInBody(
  body: Word.Body,
  pid: string,
  context: Word.RequestContext
): Promise<boolean> {
  const paras = body.paragraphs;
  paras.load("items/uniqueLocalId");
  await context.sync();
  const direct = paras.items.find(p => p.uniqueLocalId === pid);
  if (direct) { direct.getRange(Word.RangeLocation.whole).select(); await context.sync(); return true; }

  const tables = body.tables;
  tables.load("items");
  await context.sync();
  for (const table of tables.items) {
    table.rows.load("items");
    await context.sync();
    for (const row of table.rows.items) {
      row.cells.load("items");
      await context.sync();
      for (const cell of row.cells.items) {
        const cp = cell.body.paragraphs;
        cp.load("items/uniqueLocalId");
        await context.sync();
        const m = cp.items.find(p => p.uniqueLocalId === pid);
        if (m) { m.getRange(Word.RangeLocation.whole).select(); await context.sync(); return true; }
      }
    }
  }
  return false;
}

// S3776: extracted from navigateToItem to reduce its cognitive complexity
async function findInHeadersAndFooters(
  sections: Word.SectionCollection,
  paraId: string,
  context: Word.RequestContext
): Promise<boolean> {
  const hfTypes: Word.HeaderFooterType[] = [
    Word.HeaderFooterType.primary,
    Word.HeaderFooterType.firstPage,
    Word.HeaderFooterType.evenPages,
  ];
  for (const section of sections.items) {
    for (const hft of hfTypes) {
      try {
        const hdr = section.getHeader(hft);
        if (await findAndSelectInBody(hdr, paraId, context)) return true;
      } catch (e) {
        console.warn("[Nav] Header not present on section:", e);
      }
      try {
        const ftr = section.getFooter(hft);
        if (await findAndSelectInBody(ftr, paraId, context)) return true;
      } catch (e) {
        console.warn("[Nav] Footer not present on section:", e);
      }
    }
  }
  return false;
}

// navigation logic extracted so openSummary stays below complexity threshold
async function navigateToItem(bookmarkId: string, paraId: string): Promise<void> {
  await Word.run(async (context) => {
    if (bookmarkId) {
      const bmRange = context.document.getBookmarkRangeOrNullObject(bookmarkId);
      bmRange.load("isNullObject");
      await context.sync();
      if (!bmRange.isNullObject) { bmRange.select(); await context.sync(); return; }
      console.warn("[Nav] Bookmark not found:", bookmarkId);
      if (!paraId) return;
    }
    if (paraId) {
      if (await findAndSelectInBody(context.document.body, paraId, context)) return;

      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      if (await findInHeadersAndFooters(sections, paraId, context)) return;

      console.warn("[Nav] Paragraph not found anywhere:", paraId);
    }
  });
}

function attachNavRowListeners(content: HTMLElement): void {
  content.querySelectorAll(".sr-nav-row").forEach((el) => {
    el.addEventListener("click", async (e) => {
      e.stopPropagation();
      // S7761: use .dataset instead of getAttribute
      const bookmarkId = (el as HTMLElement).dataset.bookmarkId || "";
      const paraId     = (el as HTMLElement).dataset.paraId     || "";
      if (!bookmarkId && !paraId) return;

      (el as HTMLElement).classList.add("sr-nav-active");
      setTimeout(() => (el as HTMLElement).classList.remove("sr-nav-active"), 1000);

      try {
        await navigateToItem(bookmarkId, paraId);
      } catch (err) {
        console.warn("[Nav] Navigation error:", err);
      }
    });
  });
}

function openSummary(): void {
  const panel   = document.getElementById("summary-panel");
  const content = document.getElementById("summaryContent");
  if (!panel || !content) return;

  let totalRun = 0, totalDone = 0, totalErrors = 0;
  qcResults.forEach((result) => {
    if (result.status === "done") { totalDone++; totalRun++; }
    else if (result.status === "error") { totalErrors++; totalRun++; }
  });
  const pending = TOTAL_CHECKS - totalRun;

  let html = buildSummaryStatsHtml(totalRun, totalDone, pending);
  for (let i = 0; i < RUNS_CONFIG.length; i++) {
    html += buildSummaryCardHtml(RUNS_CONFIG[i], i);
  }

  content.innerHTML = html;
  panel.style.display = "block";

  content.querySelectorAll(".sr-clickable").forEach((el) => {
    el.addEventListener("click", () => {
      // S7761: use .dataset instead of getAttribute("data-id")
      const rid  = (el as HTMLElement).dataset.id;
      const body = document.getElementById("srbody-" + rid);
      const chev = content.querySelector('.sr-chev[data-id="' + rid + '"]');
      if (!body) return;
      const opening = !body.classList.contains("open");
      content.querySelectorAll(".sr-body.open").forEach((b) => b.classList.remove("open"));
      content.querySelectorAll(".sr-chev.up").forEach((c) => c.classList.remove("up"));
      if (opening) { body.classList.add("open"); if (chev) chev.classList.add("up"); }
    });
  });

  attachNavRowListeners(content);
}

// ═══════════════════════════════════════════════════════════════════════════
// UNDO / REJECT ALL TRACKED CHANGES
// ═══════════════════════════════════════════════════════════════════════════

async function undoLastChanges(): Promise<void> {
  const confirmed = await showConfirmDialog(
    "Reject All Tracked Changes?\n\nThis will REJECT every pending tracked change in the document, reverting it to its original state before QC checks.\n\nThis cannot be undone."
  );
  if (!confirmed) return;

  try {
    await Word.run(async (context) => {
      const body = context.document.body;
      const trackedChanges = body.getTrackedChanges();
      trackedChanges.load("items"); await context.sync();

      const count = trackedChanges.items.length;
      if (count === 0) { safeAlert("No tracked changes found in the document."); return; }

      console.log("[Undo] Rejecting " + count + " tracked changes...");
      for (let i = count - 1; i >= 0; i--) { trackedChanges.items[i].reject(); }
      await context.sync();

      const bookmarkNames: string[] = [];
      qcResults.forEach((result) => {
        if (result.details) {
          for (const d of result.details) {
            if (d.bookmarkId) bookmarkNames.push(d.bookmarkId);
          }
        }
      });
      let bmCleaned = 0;
      for (const bmName of bookmarkNames) {
        try {
          context.document.deleteBookmark(bmName);
          bmCleaned++;
        } catch (bmErr) {
          console.warn("[Undo] Bookmark already gone or could not be deleted:", bmName, bmErr);
        }
      }
      if (bmCleaned > 0) { await context.sync(); console.log("[Undo] Deleted " + bmCleaned + " bookmark(s)."); }

      qcResults.forEach((result, id) => {
        result.status = "pending"; result.message = ""; result.details = []; result.timestamp = null; result.durationMs = undefined;
        const badge = document.getElementById("status-" + id);
        if (badge) { badge.className = "check-status"; badge.textContent = ""; badge.title = ""; }
      });

      console.log("[Undo] \u2713 Rejected " + count + " tracked changes.");
      safeAlert("Rejected " + count + " tracked changes.\n\nAll QC modifications have been reverted.");
    });
  } catch (error) {
    console.error("[Undo] Error:", error);
    safeAlert("Could not auto-reject changes.\n\nTo undo manually:\n1. Go to Review tab in Word\n2. Click Reject \u2192 Reject All Changes\n\nError: " + (error as Error).message);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// CHECK SEARCH — filters .ck-card visibility based on label / number / id
// ═══════════════════════════════════════════════════════════════════════════

function setupChecksSearch(): void {
  const input    = document.getElementById("checksSearchInput") as HTMLInputElement | null;
  const clearBtn = document.getElementById("checksSearchClear") as HTMLButtonElement | null;
  const wrap     = document.querySelector(".checks-search-wrap") as HTMLElement | null;
  const emptyMsg = document.getElementById("checks-search-empty");
  if (!input || !clearBtn || !wrap) return;

  const selectAllCb = document.getElementById("selectAllCb") as HTMLInputElement | null;

  const applyFilter = (): void => {
    const raw   = input.value.trim().toLowerCase();
    const cards = document.querySelectorAll(".ck-card") as NodeListOf<HTMLElement>;
    let visibleCount = 0;

    cards.forEach((card) => {
      if (!raw) {
        card.classList.remove("search-hidden");
        visibleCount++;
        return;
      }
      // S7761: use .dataset instead of getAttribute("data-search")
      const idx   = card.dataset.search || "";
      const match = idx.includes(raw);
      card.classList.toggle("search-hidden", !match);
      if (match) visibleCount++;
    });

    wrap.classList.toggle("has-text", raw.length > 0);
    if (emptyMsg) emptyMsg.style.display = (raw && visibleCount === 0) ? "block" : "none";

    if (selectAllCb) {
      const visibleCbs = Array.from(
        document.querySelectorAll(".ck-card")
      ).filter((c) => !(c as HTMLElement).classList.contains("search-hidden"))
       .map((c) => (c as HTMLElement).querySelector(".run-cb") as HTMLInputElement)
       .filter((cb) => !!cb);

      if (visibleCbs.length === 0) {
        selectAllCb.checked = false;
        selectAllCb.indeterminate = false;
      } else {
        const allChecked  = visibleCbs.every((c) => c.checked);
        const noneChecked = visibleCbs.every((c) => !c.checked);
        selectAllCb.checked = allChecked;
        selectAllCb.indeterminate = !allChecked && !noneChecked;
      }
    }
  };

  input.addEventListener("input", applyFilter);
  input.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Escape" && input.value) {
      input.value = "";
      applyFilter();
      input.blur();
    }
  });
  clearBtn.addEventListener("click", () => {
    input.value = "";
    applyFilter();
    input.focus();
  });
}

// Clears every run checkbox + the "Select All" box (incl. its indeterminate
// state), then refreshes the Run-Selected button. Called after a batch run so
// the selection doesn't linger once the checks have completed.
function clearCheckSelection(): void {
  document.querySelectorAll(".run-cb").forEach((cb) => {
    (cb as HTMLInputElement).checked = false;
  });
  const selectAllCb = document.getElementById("selectAllCb") as HTMLInputElement | null;
  if (selectAllCb) {
    selectAllCb.checked = false;
    selectAllCb.indeterminate = false;
  }
  updateRunSelectedBtn();
}

function updateRunSelectedBtn(): void {
  const btn = document.getElementById("runSelectedBtn") as HTMLButtonElement;
  if (!btn) return;
  const count = document.querySelectorAll(".run-cb:checked").length;
  btn.disabled = count === 0;
  // S3358: extract inner ternary to named variable
  const plural = count > 1 ? "s" : "";
  btn.textContent = count > 0
    ? "\u25B6 Run " + count + " Selected Check" + plural
    : "\u25B6 Run Selected Checks";
}

// ═══════════════════════════════════════════════════════════════════════════
// RUN SELECTED CHECKS
// ═══════════════════════════════════════════════════════════════════════════

// S3776: complexity reduced by extracting helper — collect selected IDs separately
function getSelectedRunIds(): string[] {
  // Fix for TS2495: NodeListOf is not directly iterable with for…of; use Array.from
  const checkedCbs = Array.from(
    document.querySelectorAll(".run-cb:checked") as NodeListOf<HTMLInputElement>
  );
  const ids: string[] = [];
  for (const cb of checkedCbs) {
    // S7761: use .dataset instead of getAttribute("data-run")
    const runId = cb.dataset.run;
    if (runId) ids.push(runId);
  }
  return ids;
}

async function runSelectedChecks(): Promise<void> {
  const selectedIds = getSelectedRunIds();
  if (selectedIds.length === 0) {
    safeAlert("No checks selected.\n\nPlease select at least one check using the checkboxes.");
    return;
  }

  console.log("=".repeat(70));
  console.log("=== RUNNING " + selectedIds.length + " SELECTED CHECKS (queued) ===");
  console.log("=".repeat(70));

  await measureDocumentComplexity(true).catch(() => { /* already logged */ });

  const btn = document.getElementById("runSelectedBtn") as HTMLButtonElement;
  if (btn) { btn.disabled = true; btn.textContent = "\u23F3 Running..."; }

  for (const id of selectedIds) {
    const fn = RUN_FUNCTIONS.get(id);
    if (!fn) continue;
    try {
      await safeExecuteRun(id, fn);
    } catch (e) {
      console.error(`runSelectedChecks: unexpected error after ${id}:`, e);
    }
  }

  // Uncheck all boxes now that the batch is done (also re-enables/relabels the button).
  clearCheckSelection();
  console.log("=== BATCH COMPLETE: " + selectedIds.length + " checks processed ===");
}